import { useEffect, useRef, useState } from "react";

import Modal from "../../../components/Modal/Modal";
import DateInput from "../../../components/input/DateInput/DateInput";
import SelectInput from "../../../components/input/SelectInput/SelectInput";
import TextInput from "../../../components/input/TextInput";
import { generateLicence, uploadLicenceSvg } from "../../../services/licence.service";
import { getUtcOffset } from "../../../utils/functions";
import { runGetCatalogs, runGetTemplate } from "../../../utils/services";
import styles from "./Generate.module.scss";

const initialFormData = {
	cliente: "",
	nit: "",
	contacto: "",
	cargo: "",
	telefono: "",
	correo: "",
	departamento: "",
	ciudad: "",
	direccion: "",
	serialBiometrico: "",
	modeloDispositivo: "",
	excepcion: "false",
	fechaCompra: "",
	tipoLicencia: "",
	fechaExpiracion: "",
};

const EXCEPTION_DEVICE_MODEL = "MSO1300E2";

let cachedDeviceModels = [];
let cachedLicenseTypes = [];

const buildGeneratePayload = (formData) => ({
	company: formData.cliente,
	taxId: formData.nit,
	contactName: formData.contacto,
	position: formData.cargo,
	phone: formData.telefono,
	city: formData.ciudad,
	state: formData.departamento,
	address: formData.direccion,
	email: formData.correo,
	serial: formData.serialBiometrico,
	type: formData.tipoLicencia,
	deviceModel: formData.modeloDispositivo,
	exception: formData.excepcion === "true",
	expirationDate: formData.fechaExpiracion,
	purchaseDate: formData.fechaCompra,
	utcOffset: getUtcOffset(),
});

const translateGenerateCode = (code, fallbackMessage = "") => {
	switch (code) {
		case "LRG001":
			return { type: "success", message: "Operacion exitosa." };
		case "LRG002":
			return {
				type: "error",
				message:
					fallbackMessage ||
					"Faltan parametros obligatorios en el formulario o hay campos con formato incorrecto.",
			};
		case "LRT003":
			return { type: "error", message: "El tipo de licencia ingresado no es valido." };
		case "LRT005":
			return {
				type: "error",
				message:
					"Ya existe una licencia registrada para ese numero de serial y tipo de equipo.",
			};
		case "LRT008":
			return {
				type: "error",
				message:
					"El modelo del dispositivo no es valido o no esta homologado.",
			};
		case "LRL008":
			return { type: "error", message: "Su sesion ha expirado." };
		default:
			return {
				type: "error",
				message:
					fallbackMessage ||
					"Ocurrio un error al generar la licencia. Intentelo nuevamente.",
			};
	}
};

const translateCsvRowError = (code, fallbackMessage = "") => {
	switch (code) {
		case "LRT011":
			return "Fecha de compra no valida.";
		case "LRT012":
			return "Fecha de vencimiento no valida.";
		case "LRT013":
			return "Serial no valido.";
		case "LRT014":
			return "Tipo de licencia no valido.";
		default:
			return fallbackMessage || "Fila con error.";
	}
};

const buildCsvUploadSummary = (uploadResult = {}) => {
	const failedRows = uploadResult.failedRows || [];
	const loadedRows = uploadResult.loadedRows || [];
	const failedCount = uploadResult.failed ?? failedRows.length ?? 0;
	const loadedCount = uploadResult.loaded ?? loadedRows.length ?? 0;
	const totalCount = uploadResult.total ?? failedCount + loadedCount;

	return {
		totalCount,
		loadedCount,
		failedCount,
		failedRows: failedRows.map((row) => ({
			row: row?.row ?? null,
			serial: row?.serial ?? "",
			code: row?.code ?? "",
			message: translateCsvRowError(row?.code, row?.error),
		})),
	};
};

const Generate = () => {
	const [deviceModels, setDeviceModels] = useState([]);
	const [licenseTypes, setLicenseTypes] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
	const [isUploadingCsv, setIsUploadingCsv] = useState(false);
	const [responseMessage, setResponseMessage] = useState("");
	const [responseType, setResponseType] = useState("");
	const [generatedLicense, setGeneratedLicense] = useState(null);
	const [csvFile, setCsvFile] = useState(null);
	const [csvModalOpen, setCsvModalOpen] = useState(false);
	const [csvUploadSummary, setCsvUploadSummary] = useState(null);
	const [formData, setFormData] = useState(initialFormData);
	const csvInputRef = useRef(null);

	useEffect(() => {
		if (cachedDeviceModels.length > 0 || cachedLicenseTypes.length > 0) {
			setDeviceModels(cachedDeviceModels);
			setLicenseTypes(cachedLicenseTypes);
			return;
		}

		let isMounted = true;

		const loadCatalogs = async () => {
			setIsLoadingCatalogs(true);

			try {
				const response = await runGetCatalogs();
				const catalogs = response?.message || {};

				if (!isMounted) return;

				cachedDeviceModels = catalogs.deviceModels || [];
				cachedLicenseTypes = catalogs.typeLicenses || [];
				setDeviceModels(cachedDeviceModels);
				setLicenseTypes(cachedLicenseTypes);
			} catch (error) {
				if (!isMounted) return;
				setResponseType("error");
				setResponseMessage(
					error?.code === "LRL008"
						? "Su sesion ha expirado."
						: error?.message ||
								"No se pudieron cargar los catalogos de licencias.",
				);
			} finally {
				if (isMounted) {
					setIsLoadingCatalogs(false);
				}
			}
		};

		loadCatalogs();

		return () => {
			isMounted = false;
		};
	}, []);

	const setResponse = ({ type, message }) => {
		setResponseType(type);
		setResponseMessage(message);
	};

	const handleFieldChange = (event) => {
		const { name, value } = event.target;
		setResponseMessage("");
		setResponseType("");

		setFormData((current) => {
			const updatedData = {
				...current,
				[name]: value,
			};

			if (
				name === "modeloDispositivo" &&
				value !== EXCEPTION_DEVICE_MODEL
			) {
				updatedData.excepcion = "false";
			}

			if (name === "fechaCompra" && value) {
				const purchaseDate = new Date(value);
				purchaseDate.setFullYear(purchaseDate.getFullYear() + 10);
				updatedData.fechaExpiracion = purchaseDate
					.toISOString()
					.split("T")[0];
			}

			return updatedData;
		});
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setResponseMessage("");
		setResponseType("");
		setGeneratedLicense(null);
		setIsSubmitting(true);

		try {
			const response = await generateLicence(buildGeneratePayload(formData));
			const responseCode = response?.code || response?.codigo || response;
			const responseLicense = response?.message?.license || null;

			setResponse(
				translateGenerateCode(
					responseCode,
					typeof response?.message === "string"
						? response.message
						: response?.mensaje,
				),
			);

			if (responseCode === "LRG001" && responseLicense) {
				setGeneratedLicense(responseLicense);
				setFormData(initialFormData);
				setCsvFile(null);
				setCsvModalOpen(false);
				if (csvInputRef.current) {
					csvInputRef.current.value = "";
				}
			}
		} catch (error) {
			setResponse(translateGenerateCode(error?.code, error?.message));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDownloadTemplate = async () => {
		setResponseMessage("");
		setResponseType("");

		try {
			const response = await runGetTemplate();
			const templateContent =
				typeof response === "string"
					? response
					: response?.message?.template ||
						response?.message?.content ||
						response?.message ||
						"";

			if (!templateContent) {
				setResponseType("error");
				setResponseMessage("No se pudo descargar la plantilla CSV.");
				return;
			}

			const blob = new Blob([templateContent], {
				type: "text/csv;charset=utf-8",
			});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			const downloadDate = new Date().toISOString().split("T")[0];

			link.href = url;
			link.download = `plantilla_licencia_${downloadDate}.csv`;
			link.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			setResponseType("error");
			setResponseMessage(
				error?.code === "LRL008"
					? "Su sesion ha expirado."
					: error?.message || "No se pudo descargar la plantilla CSV.",
			);
		}
	};

	const handleSelectCsvFile = () => {
		setCsvUploadSummary(null);
		if (csvInputRef.current) {
			csvInputRef.current.value = "";
		}
		csvInputRef.current?.click();
	};

	const handleCsvInputChange = (event) => {
		const nextFile = event.target.files?.[0] || null;

		setCsvFile(nextFile);
		setCsvModalOpen(Boolean(nextFile));
		setResponseMessage("");
		setResponseType("");
	};

	const handleCloseCsvModal = () => {
		setCsvModalOpen(false);
		setCsvFile(null);
		if (csvInputRef.current) {
			csvInputRef.current.value = "";
		}
	};

	const handleChangeCsvFile = () => {
		setCsvUploadSummary(null);
		if (csvInputRef.current) {
			csvInputRef.current.value = "";
		}
		csvInputRef.current?.click();
	};

	const handleUploadCsv = async () => {
		if (!csvFile) {
			setResponseType("error");
			setResponseMessage("No hay archivo CSV seleccionado.");
			return;
		}

		setCsvUploadSummary(null);
		setIsUploadingCsv(true);

		try {
			const payload = new FormData();
			payload.append("file", csvFile);

			const response = await uploadLicenceSvg(payload);
			const uploadResult = response?.message || {};
			const summary = buildCsvUploadSummary(uploadResult);
			setCsvUploadSummary(summary);

			if (summary.failedCount > 0) {
				setResponseType("error");
				setResponseMessage("Archivo con errores.");
				return;
			}

			setResponseType("success");
			setResponseMessage(summary.loadedCount > 0 ? "CSV cargado." : "CSV cargado.");
			setCsvUploadSummary(null);
			handleCloseCsvModal();
		} catch (error) {
			setResponseType("error");
			setResponseMessage(
				error?.code === "LRL008"
					? "Su sesion ha expirado."
					: error?.message || "No se pudo cargar el archivo CSV.",
			);
		} finally {
			setIsUploadingCsv(false);
		}
	};

	const formatFileSize = (sizeInBytes = 0) => {
		if (!sizeInBytes) return "0 bytes";
		if (sizeInBytes < 1024) return `${sizeInBytes} bytes`;
		if (sizeInBytes < 1024 * 1024) {
			return `${(sizeInBytes / 1024).toFixed(1)} KB`;
		}
		return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	const formatLicenseDownload = (licenseData) => {
		if (!licenseData) return "";

		return Object.entries(licenseData)
			.map(([key, value]) => `${key}: ${value ?? ""}`)
			.join("\n");
	};

	const getLicenseFieldLabel = (key) => {
		const labels = {
			company: "Empresa",
			taxId: "NIT",
			contactName: "Nombre de contacto",
			position: "Cargo",
			phone: "Teléfono",
			city: "Ciudad",
			state: "Departamento",
			address: "Dirección",
			email: "Correo electrónico",
			serial: "Serial",
			purchaseDate: "Fecha de compra",
			expirationDate: "Fecha de vencimiento",
			license: "Licencia",
			type: "Tipo de licencia",
			deviceModel: "Modelo de dispositivo",
			status: "Estado",
			generatedBy: "Generada por",
			updatedBy: "Actualizada por",
			createdAt: "Creada el",
			updatedAt: "Actualizada el",
			_id: "Identificador",
		};

		return labels[key] || key;
	};

	const handleDownloadLicense = () => {
		if (!generatedLicense) return;

		const blob = new Blob([formatLicenseDownload(generatedLicense)], {
			type: "text/plain;charset=utf-8",
		});
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");

		link.href = url;
		link.download = `licencia_${generatedLicense.serial || "generada"}.txt`;
		link.click();
		window.URL.revokeObjectURL(url);
	};

	const deviceModelOptions = deviceModels.map((deviceModel) => ({
		value: deviceModel.value,
		label: deviceModel.name,
	}));

	const licenseTypeOptions = licenseTypes.map((licenseType) => ({
		value: licenseType.value,
		label: licenseType.name,
	}));

	const exceptionOptions = [
		{ value: "false", label: "No aplica" },
		{ value: "true", label: "Aplica" },
	];
	const isExceptionEnabled =
		formData.modeloDispositivo === EXCEPTION_DEVICE_MODEL;

	return (
		<div className={styles.registroWrapper}>
			<form onSubmit={handleSubmit} className={styles.formBody}>
				<section className={styles.formSection}>
					<h3 className={styles.sectionHeading}>1. Datos de la empresa</h3>
					<div className={styles.inputsGrid}>
						<TextInput
							name="cliente"
							label="Cliente o empresa *"
							required
							placeholder="Razon social"
							value={formData.cliente}
							onChange={(value) =>
								handleFieldChange({ target: { name: "cliente", value } })
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
						<TextInput
							name="nit"
							label="NIT *"
							required
							placeholder="Ej: 900123456"
							value={formData.nit}
							onChange={(value) =>
								handleFieldChange({ target: { name: "nit", value } })
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
						<TextInput
							name="departamento"
							label="Departamento *"
							required
							placeholder="Ej: Cundinamarca"
							value={formData.departamento}
							onChange={(value) =>
								handleFieldChange({ target: { name: "departamento", value } })
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
						<TextInput
							name="ciudad"
							label="Ciudad *"
							required
							placeholder="Ej: Bogota D.C."
							value={formData.ciudad}
							onChange={(value) =>
								handleFieldChange({ target: { name: "ciudad", value } })
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
						<TextInput
							name="direccion"
							label="Direccion *"
							required
							placeholder="Direccion completa"
							value={formData.direccion}
							onChange={(value) =>
								handleFieldChange({ target: { name: "direccion", value } })
							}
							className={styles.fieldGroupWide}
							inputClassName={styles.fieldInput}
						/>
					</div>
				</section>

				<section className={styles.formSection}>
					<h3 className={styles.sectionHeading}>2. Datos de contacto</h3>
					<div className={styles.inputsGrid}>
						<TextInput
							name="contacto"
							label="Contacto *"
							required
							placeholder="Nombre del contacto"
							value={formData.contacto}
							onChange={(value) =>
								handleFieldChange({ target: { name: "contacto", value } })
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
						<TextInput
							name="cargo"
							label="Cargo *"
							required
							placeholder="Cargo"
							value={formData.cargo}
							onChange={(value) =>
								handleFieldChange({ target: { name: "cargo", value } })
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
						<TextInput
							name="telefono"
							label="Telefono *"
							required
							placeholder="Telefono"
							value={formData.telefono}
							onChange={(value) =>
								handleFieldChange({ target: { name: "telefono", value } })
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
						<TextInput
							type="email"
							name="correo"
							label="Correo electronico *"
							required
							placeholder="correo@empresa.com"
							value={formData.correo}
							onChange={(value) =>
								handleFieldChange({ target: { name: "correo", value } })
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
					</div>
				</section>

				<section className={styles.formSection}>
					<h3 className={styles.sectionHeading}>3. Hardware y licencia</h3>
					<div className={styles.inputsGrid}>
						<TextInput
							name="serialBiometrico"
							label="Serial del dispositivo *"
							required
							placeholder="1234I123456"
							value={formData.serialBiometrico}
							onChange={(value) =>
								handleFieldChange({
									target: { name: "serialBiometrico", value },
								})
							}
							className={styles.fieldGroup}
							inputClassName={styles.fieldInput}
						/>
						<SelectInput
							label="Modelo de dispositivo *"
							name="modeloDispositivo"
							required
							value={formData.modeloDispositivo}
							onChange={handleFieldChange}
							options={deviceModelOptions}
							placeholder={
								isLoadingCatalogs
									? "Cargando modelos..."
									: "Seleccione un modelo"
							}
							disabled={isLoadingCatalogs}
						/>
						<SelectInput
							label="Tipo de licencia *"
							name="tipoLicencia"
							required
							value={formData.tipoLicencia}
							onChange={handleFieldChange}
							options={licenseTypeOptions}
							placeholder={
								isLoadingCatalogs
									? "Cargando tipos..."
									: "Seleccione un tipo"
							}
							disabled={isLoadingCatalogs}
						/>
						<SelectInput
							label="Excepción *"
							name="excepcion"
							required={isExceptionEnabled}
							value={formData.excepcion}
							onChange={handleFieldChange}
							options={exceptionOptions}
							placeholder="Seleccione si aplica excepción"
							disabled={!isExceptionEnabled}
						/>
						<DateInput
							label="Fecha de compra *"
							name="fechaCompra"
							required
							value={formData.fechaCompra}
							onChange={handleFieldChange}
						/>
						<DateInput
							label="Fecha de vencimiento *"
							name="fechaExpiracion"
							required
							value={formData.fechaExpiracion}
							onChange={handleFieldChange}
						/>
					</div>
				</section>

				<div className={styles.actionsRow}>
					<button
						type="button"
						className={`${styles.secondaryBtn} ${styles.templateDownloadButton}`}
						onClick={handleDownloadTemplate}
					>
						Descargar plantilla CSV
					</button>

					<div
						className={`${styles.responseBox} ${
							responseMessage ? styles.responseBoxVisible : ""
						} ${
							responseType === "success"
								? styles.responseBoxSuccess
								: styles.responseBoxError
						}`}
						role="alert"
						aria-live="polite"
					>
						{responseMessage}
					</div>

					<input
						ref={csvInputRef}
						type="file"
						accept=".csv,text/csv"
						onChange={handleCsvInputChange}
						className={styles.csvInput}
					/>

					<button
						type="button"
						className={styles.secondaryBtn}
						onClick={handleSelectCsvFile}
					>
						{csvFile ? "Cambiar CSV" : "Seleccionar CSV"}
					</button>

					<button
						type="submit"
						className={styles.submitBtn}
						disabled={isSubmitting}
					>
						{isSubmitting ? "Generando..." : "Guardar y activar licencia"}
					</button>
				</div>
			</form>

			{csvModalOpen && csvFile ? (
				<Modal
					title="Archivo CSV seleccionado"
					onClose={handleCloseCsvModal}
					hideFooterButton
					hideIcon
				>
					<div className={styles.csvSummary}>
						<div className={styles.csvFileHero}>
							<span className={styles.csvFileBadge}>CSV</span>
							<div className={styles.csvFileInfo}>
								<span className={styles.csvFileLabel}>Archivo seleccionado</span>
								<strong className={styles.csvFileName}>{csvFile.name}</strong>
							</div>
						</div>

						<p className={styles.modalIntro}>
							Revise que este sea el archivo correcto antes de enviarlo.
						</p>
						<div className={styles.csvDataGrid}>
							<div className={styles.dataItem}>
								<span className={styles.dataLabel}>Formato</span>
								<span className={styles.dataValue}>
									{csvFile.type || "text/csv"}
								</span>
							</div>
							<div className={styles.dataItem}>
								<span className={styles.dataLabel}>Tamano</span>
								<span className={styles.dataValue}>
									{formatFileSize(csvFile.size)}
								</span>
							</div>
						</div>

						{csvUploadSummary?.failedCount > 0 ? (
							<div
								className={styles.csvUploadSummaryBox}
								role="alert"
								aria-live="polite"
							>
								<div className={styles.csvUploadSummaryHeader}>
									<strong className={styles.csvUploadSummaryTitle}>
										Archivo con errores
									</strong>
									<span className={styles.csvUploadSummaryMeta}>
										{csvUploadSummary.failedCount} fallidas de{" "}
										{csvUploadSummary.totalCount}
									</span>
								</div>
								<div className={styles.csvUploadSummaryList}>
									{csvUploadSummary.failedRows.map((rowItem) => (
										<div
											className={styles.csvUploadSummaryItem}
											key={`${rowItem.row}-${rowItem.code}`}
										>
											<span className={styles.csvUploadSummaryItemRow}>
												Fila {rowItem.row ?? "-"}
											</span>
											<span className={styles.csvUploadSummaryItemText}>
												{rowItem.message}
											</span>
										</div>
									))}
								</div>
							</div>
						) : null}

						<div className={styles.csvActions}>
							<button
								type="button"
								className={styles.secondaryBtn}
								onClick={handleCloseCsvModal}
							>
								Cancelar
							</button>
							<button
								type="button"
								className={styles.secondaryBtn}
								onClick={handleChangeCsvFile}
							>
								Cambiar documento
							</button>
							<button
								type="button"
								className={styles.downloadBtn}
								onClick={handleUploadCsv}
								disabled={isUploadingCsv}
							>
								{isUploadingCsv ? "Enviando..." : "Enviar"}
							</button>
						</div>
					</div>
				</Modal>
			) : null}

			{generatedLicense ? (
				<Modal
					title="Licencia generada"
					onClose={() => setGeneratedLicense(null)}
					hideIcon
					hideFooterButton
				>
					<div className={styles.licenseSummary}>
						<div className={styles.licenseHero}>
							<div className={styles.licenseHeroCopy}>
								<span className={styles.licenseHeroLabel}>Resultado exitoso</span>
								<strong className={styles.licenseHeroTitle}>
									La licencia fue generada correctamente
								</strong>
								<p className={styles.modalIntro}>
									Revise la informacion antes de descargar el archivo TXT.
								</p>
							</div>
							<div className={styles.licenseHeroBadge}>OK</div>
						</div>

						<div className={styles.licenseDataGrid}>
							{Object.entries(generatedLicense).map(([key, value]) => (
								<div className={styles.dataItem} key={key}>
									<span className={styles.dataLabel}>
										{getLicenseFieldLabel(key)}
									</span>
									<span className={styles.dataValue}>{String(value ?? "")}</span>
								</div>
							))}
						</div>
						<div className={styles.licenseActions}>
							<button
								type="button"
								className={styles.secondaryBtn}
								onClick={() => setGeneratedLicense(null)}
							>
								Cerrar
							</button>
							<button
								type="button"
								className={styles.downloadBtn}
								onClick={handleDownloadLicense}
							>
								Descargar TXT
							</button>
						</div>
					</div>
				</Modal>
			) : null}
		</div>
	);
};

export default Generate;
